import useSWR from 'swr'

import { Country } from '@/core/services/types/getCountries/responseTypes'
import { getCountriesFetcher } from '@/functions/fetchers/getCountries'

export function useGetCountries() {
  const CACHE_DURATION = 1 * 60 * 60 * 1000 // 1 hour in milliseconds

  const getInitialData = () => {
    if (typeof window === 'undefined') return undefined
    try {
      const cached = localStorage.getItem('countries-cache')
      if (!cached) return undefined

      const parsedCache = JSON.parse(cached)

      // Check if this is legacy data (array or object without timestamp)
      if (!parsedCache.timestamp || typeof parsedCache.timestamp !== 'number') {
        // Remove legacy cache data and return undefined to force fresh fetch
        localStorage.removeItem('countries-cache')
        return undefined
      }

      const { data, timestamp } = parsedCache
      const now = Date.now()

      // Check if cache is older than 3 hours
      if (now - timestamp > CACHE_DURATION) {
        localStorage.removeItem('countries-cache')
        return undefined
      }

      return data
    } catch {
      // If parsing fails or any other error occurs, remove cache and return undefined
      localStorage.removeItem('countries-cache')
      return undefined
    }
  }

  const { data, error, isLoading } = useSWR({ url: 'getCountries' }, getCountriesFetcher, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    revalidateIfStale: false,
    fallbackData: getInitialData(),
    onSuccess: (data) => {
      if (typeof window !== 'undefined') {
        try {
          const cacheData = {
            data,
            timestamp: Date.now()
          }
          localStorage.setItem('countries-cache', JSON.stringify(cacheData))
        } catch (error) {
          console.error('Failed to cache countries:', error)
        }
      }
    }
  })

  const loading = isLoading || (!data && !error)

  const countriesData = data?.reduce((acc: Record<string, Country>, country) => {
    acc[country.code.toLowerCase()] = country

    return acc
  }, {})

  return {
    loading,
    countriesData,
    error
  }
}
